import React from "react";
import { Comment } from "../../../types";
import { formatDistanceToNow } from "date-fns";
import {
  ArrowTopRightOnSquareIcon,
  MapPinIcon,
} from "@heroicons/react/24/outline";

interface CommentListProps {
  comments: Comment[];
  activeTab: "active" | "resolved";
  onCommentClick: (comment: Comment) => void;
}

export const CommentList: React.FC<CommentListProps> = ({
  comments,
  activeTab,
  onCommentClick,
}) => {
  const currentPageUrl = window.location.href;

  const filteredComments = comments.filter((c) =>
    activeTab === "active" ? c.status === "active" : c.status === "resolved"
  );

  const scrollToComment = (comment: Comment) => {
    if (comment.position?.pageUrl === currentPageUrl) {
      // Comment is on current page - close sidebar, scroll, then highlight

      if (comment.position?.isFixedElement) {
        // Fixed element - just close sidebar and let user see it
        // Don't scroll for fixed elements as they're always visible
        onCommentClick(comment);
        // Close sidebar to show comment markers
        setTimeout(() => {
          // The parent component should handle closing sidebar
          // We'll emit a custom event to trigger this
          window.dispatchEvent(
            new CustomEvent("feedbackflow:close-sidebar-and-highlight", {
              detail: { commentId: comment.id },
            })
          );
        }, 100);
      } else {
        // Regular element - close sidebar first, then scroll and highlight
        window.dispatchEvent(
          new CustomEvent("feedbackflow:close-sidebar-and-highlight", {
            detail: {
              commentId: comment.id,
              shouldScroll: true,
              x: comment.position?.pageX || 0,
              y: comment.position?.pageY || 0,
            },
          })
        );
      }
    } else {
      // Comment is on different page - navigate to it
      const url = new URL(comment.position?.pageUrl || currentPageUrl);
      url.searchParams.set("mode", "widget");
      url.searchParams.set("comment", comment.id);
      window.open(url.toString(), "_blank");
    }
  };

  const getPageName = (url: string) => {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      if (pathname === "/" || pathname === "") return "Home";
      return pathname.split("/").filter(Boolean).pop() || "Page";
    } catch {
      return "Page";
    }
  };

  return (
    <div className="divide-y divide-gray-100">
      {filteredComments.map((comment) => {
        const isOnCurrentPage = comment.position?.pageUrl === currentPageUrl;
        const pageName = getPageName(
          comment.position?.pageUrl || currentPageUrl
        );

        return (
          <div key={comment.id} className="p-4 hover:bg-gray-50">
            <div className="flex flex-col gap-2">
              {/* Header */}
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-full bg-purple-500 flex items-center justify-center">
                  <span
                    className="text-xs font-medium"
                    style={{ color: "#ffffff" }}
                  >
                    {comment.number}
                  </span>
                </div>
                <span className="text-sm font-medium">{comment.author}</span>
                {!comment.isRead && comment.status === "active" && (
                  <span
                    className="h-5 px-2 flex items-center text-xs font-medium bg-[#FF7062] rounded-full"
                    style={{ color: "#ffffff" }}
                  >
                    New
                  </span>
                )}
                {!isOnCurrentPage && (
                  <span className="h-5 px-2 flex items-center text-xs font-medium text-blue-600 bg-blue-100 rounded-full">
                    {pageName}
                  </span>
                )}
              </div>

              {/* Timestamp */}
              <p className="text-xs text-gray-500">
                {formatDistanceToNow(new Date(comment.timestamp), {
                  addSuffix: true,
                })}
              </p>

              {/* Comment text */}
              <p className="text-sm text-gray-900">{comment.text}</p>

              {/* Actions */}
              <div className="flex gap-2 mt-2">
                <button
                  onClick={() => onCommentClick(comment)}
                  className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded"
                >
                  <MapPinIcon className="w-3 h-3" />
                  View Details
                </button>

                <button
                  onClick={() => scrollToComment(comment)}
                  className="flex items-center gap-1 px-2 py-1 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                >
                  {isOnCurrentPage ? (
                    <>
                      <MapPinIcon className="w-3 h-3" />
                      Go to Location
                    </>
                  ) : (
                    <>
                      <ArrowTopRightOnSquareIcon className="w-3 h-3" />
                      Open Page
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
